import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { SourceOfTruth as SourceOfTruthType } from '../../types';
import { Database, Upload, Globe, CheckCircle, AlertCircle, Loader2, Plus, X, ShoppingBag, RefreshCw, FileText, Search, Bell } from 'lucide-react';
import { shopifyCloudService, ShopifyProduct } from '../../services/shopify/shopifyCloudService';
import { sftpCloudService } from '../../services/sftp/sftpCloudService';

export function SourceOfTruth() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sourceData, setSourceData] = useState<SourceOfTruthType | null>(null);
  const [connectionMethod, setConnectionMethod] = useState<'manual' | 'api' | 'sftp' | 'shopify'>('manual');

  // Shopify state
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [shopifyStoreName, setShopifyStoreName] = useState('');
  const [shopifySyncing, setShopifySyncing] = useState(false);
  const [shopifyProducts, setShopifyProducts] = useState<ShopifyProduct[]>([]);
  const [shopifyLastSync, setShopifyLastSync] = useState<Date | null>(null);

  // SFTP state
  const [sftpConnected, setSftpConnected] = useState(false);
  const [sftpTesting, setSftpTesting] = useState(false);
  const [sftpFiles, setSftpFiles] = useState<any[]>([]);
  const [sftpSyncing, setSftpSyncing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');

  // Form state
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [foundingDate, setFoundingDate] = useState('');
  const [headquarters, setHeadquarters] = useState('');
  const [employees, setEmployees] = useState('');
  const [products, setProducts] = useState<string[]>(['']);
  const [pricing, setPricing] = useState<{ product: string; price: string }[]>([
    { product: '', price: '' },
  ]);
  const [keyDifferentiators, setKeyDifferentiators] = useState<string[]>(['']);
  const [certifications, setCertifications] = useState<string[]>(['']);

  useEffect(() => {
    loadSourceOfTruth();
    testShopifyConnection();
    checkSftpSettings();
  }, [user]);

  const loadSourceOfTruth = async () => {
    if (!user) return;

    try {
      const docRef = doc(db, 'sourceOfTruth', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as SourceOfTruthType;
        setSourceData(data);
        setBrandName(data.brandName);
        setDescription(data.description);
        setFoundingDate(data.foundingDate);
        setHeadquarters(data.headquarters);
        setEmployees(data.employees);
        setProducts(data.products.length > 0 ? data.products : ['']);
        setPricing(
          Object.entries(data.pricing).map(([product, price]) => ({ product, price }))
        );
        setKeyDifferentiators(
          data.keyDifferentiators.length > 0 ? data.keyDifferentiators : ['']
        );
        setCertifications(data.certifications.length > 0 ? data.certifications : ['']);
      }
    } catch (error) {
      console.error('Error loading source of truth:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const pricingObj = pricing.reduce((acc, item) => {
        if (item.product && item.price) {
          acc[item.product] = item.price;
        }
        return acc;
      }, {} as { [key: string]: string });

      const sourceOfTruth: SourceOfTruthType = {
        userId: user.uid,
        brandName,
        description,
        foundingDate,
        headquarters,
        employees,
        products: products.filter((p) => p.trim().length > 0),
        pricing: pricingObj,
        keyDifferentiators: keyDifferentiators.filter((k) => k.trim().length > 0),
        certifications: certifications.filter((c) => c.trim().length > 0),
        updatedAt: Timestamp.now(),
      };

      await setDoc(doc(db, 'sourceOfTruth', user.uid), sourceOfTruth);
      setSourceData(sourceOfTruth);
      alert('✅ Source of truth saved successfully!');
    } catch (error) {
      console.error('Error saving source of truth:', error);
      alert('❌ Failed to save. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const testShopifyConnection = async () => {
    try {
      const result = await shopifyCloudService.testConnection();
      if (result.success) {
        setShopifyConnected(true);
        setShopifyStoreName(result.storeName || '');
      }
    } catch (error) {
      console.error('Shopify connection test failed:', error);
      setShopifyConnected(false);
    }
  };

  const handleShopifySync = async () => {
    if (!user) return;

    setShopifySyncing(true);
    try {
      // Fetch all products via Cloud Functions (handles CORS)
      const products = await shopifyCloudService.getAllProducts();
      setShopifyProducts(products);

      // Sync data to Source of Truth
      if (products.length > 0) {
        const store = await shopifyCloudService.getStore();

        const productNames = products.map((p) => p.title);
        const productTypes = [...new Set(products.map((p) => p.product_type).filter(Boolean))];

        const pricingData = products.slice(0, 10).reduce((acc, product) => {
          if (product.variants && product.variants[0]) {
            acc[product.title] = `$${product.variants[0].price}`;
          }
          return acc;
        }, {} as { [key: string]: string });

        setBrandName(store.name || brandName);
        setProducts(productNames.slice(0, 20)); // Limit to first 20
        setPricing(Object.entries(pricingData).map(([product, price]) => ({ product, price })));

        if (!description) {
          setDescription(`${store.name} offers ${productTypes.join(', ')} and more.`);
        }
      }

      setShopifyLastSync(new Date());
      alert(`✅ Successfully synced ${products.length} products from Shopify!`);
    } catch (error) {
      console.error('Error syncing Shopify products:', error);
      alert(`❌ Failed to sync Shopify products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setShopifySyncing(false);
    }
  };

  const checkSftpSettings = async () => {
    if (!user) return;

    try {
      const settingsDoc = await getDoc(doc(db, 'integrationSettings', user.uid));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.sftp?.enabled) {
          setSftpConnected(true);
        }
      }
    } catch (error) {
      console.error('Error checking SFTP settings:', error);
    }
  };

  const testSftpConnection = async () => {
    setSftpTesting(true);
    try {
      const result = await sftpCloudService.testConnection();
      if (result.success) {
        setSftpConnected(true);
        alert(`✅ ${result.message}`);
        loadSftpFiles();
      } else {
        setSftpConnected(false);
        alert(`❌ Connection failed: ${result.error}`);
      }
    } catch (error) {
      console.error('SFTP connection test failed:', error);
      setSftpConnected(false);
      alert(`❌ Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSftpTesting(false);
    }
  };

  const loadSftpFiles = async () => {
    try {
      const files = await sftpCloudService.listFiles();
      setSftpFiles(files);
    } catch (error) {
      console.error('Error loading SFTP files:', error);
    }
  };

  const handleSftpSync = async () => {
    if (!selectedFile) {
      alert('Please select a file to sync');
      return;
    }

    setSftpSyncing(true);
    try {
      const result = await sftpCloudService.syncFileToFirestore(selectedFile);
      alert(`✅ Successfully synced ${result.recordCount} records from ${result.fileName}!`);

      // Optionally auto-populate Source of Truth fields based on synced data
      // This would require additional logic to map the file data to form fields
    } catch (error) {
      console.error('Error syncing SFTP file:', error);
      alert(`❌ Failed to sync file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSftpSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Source of Truth</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
          <h1 className="text-3xl font-semibold text-gray-900">Source of Truth</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your brand's accurate information for AI accuracy verification
          </p>
        </div>

      {/* Connection Method Selector */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Connection Method</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setConnectionMethod('manual')}
            className={`p-6 rounded-card border-2 text-left transition-all ${
              connectionMethod === 'manual'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Database className="w-8 h-8 text-primary-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Manual Entry</h3>
            <p className="text-sm text-gray-600">Enter your brand information manually</p>
            {connectionMethod === 'manual' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-primary-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            )}
          </button>

          <button
            onClick={() => setConnectionMethod('shopify')}
            className={`p-6 rounded-card border-2 text-left transition-all ${
              connectionMethod === 'shopify'
                ? 'border-primary-500 bg-primary-50'
                : shopifyConnected
                ? 'border-green-300 hover:border-green-400 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <ShoppingBag className={`w-8 h-8 mb-3 ${shopifyConnected ? 'text-green-600' : 'text-primary-600'}`} />
            <h3 className="font-semibold text-gray-900 mb-2">Shopify Store</h3>
            <p className="text-sm text-gray-600">
              {shopifyConnected ? `Connected: ${shopifyStoreName}` : 'Connect your Shopify store'}
            </p>
            {connectionMethod === 'shopify' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-primary-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            )}
            {shopifyConnected && connectionMethod !== 'shopify' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Connected</span>
              </div>
            )}
          </button>

          <button
            onClick={() => setConnectionMethod('api')}
            className={`p-6 rounded-card border-2 text-left transition-all ${
              connectionMethod === 'api'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300 border-dashed opacity-60'
            }`}
            disabled
          >
            <Globe className="w-8 h-8 text-gray-400 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">CMS API Connection</h3>
            <p className="text-sm text-gray-600">
              Connect to WordPress, Contentful, Webflow, etc.
            </p>
            <div className="mt-3 flex items-center gap-2 text-sm text-blue-600">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Tool 2 Feature</span>
            </div>
          </button>

          <button
            onClick={() => setConnectionMethod('sftp')}
            className={`p-6 rounded-card border-2 text-left transition-all ${
              connectionMethod === 'sftp'
                ? 'border-primary-500 bg-primary-50'
                : sftpConnected
                ? 'border-green-300 hover:border-green-400 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Upload className={`w-8 h-8 mb-3 ${sftpConnected ? 'text-green-600' : 'text-primary-600'}`} />
            <h3 className="font-semibold text-gray-900 mb-2">SFTP / File Upload</h3>
            <p className="text-sm text-gray-600">
              {sftpConnected ? 'SFTP configured' : 'Upload static content files or connect via SFTP'}
            </p>
            {connectionMethod === 'sftp' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-primary-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Active</span>
              </div>
            )}
            {sftpConnected && connectionMethod !== 'sftp' && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Connected</span>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Tool 2 Roadmap Info */}
      {connectionMethod !== 'manual' && (
        <div className="bg-blue-50 border border-blue-200 rounded-card p-6">
          <h3 className="font-semibold text-blue-900 mb-2">
            🚀 Tool 2: Source of Truth Connector (Roadmap)
          </h3>
          <p className="text-sm text-blue-800 mb-4">
            Automated CMS integration will pull your brand data directly from your website and keep
            it synchronized. This eliminates manual updates and ensures AI always has access to your
            latest information.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-blue-900 mb-2">CMS API Connectors:</p>
              <ul className="space-y-1 text-blue-800">
                <li>• WordPress REST API</li>
                <li>• Contentful GraphQL</li>
                <li>• Webflow API</li>
                <li>• Shopify Admin API</li>
                <li>• Custom headless CMS</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-blue-900 mb-2">File-Based Options:</p>
              <ul className="space-y-1 text-blue-800">
                <li>• SFTP sync for static sites</li>
                <li>• Structured data (JSON-LD) import</li>
                <li>• CSV/Excel upload</li>
                <li>• Sitemap.xml parsing</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Shopify Sync Panel */}
      {connectionMethod === 'shopify' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Shopify Store Integration</h2>
              <p className="text-gray-600">
                Sync products, pricing, and inventory from your Shopify store
              </p>
            </div>
            {shopifyConnected && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Connected
              </div>
            )}
          </div>

          {shopifyConnected ? (
            <>
              <div className="bg-gray-50 rounded-card p-6 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Store Name</p>
                    <p className="font-semibold text-gray-900">{shopifyStoreName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Products Synced</p>
                    <p className="font-semibold text-gray-900">{shopifyProducts.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Last Sync</p>
                    <p className="font-semibold text-gray-900">
                      {shopifyLastSync ? shopifyLastSync.toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Status</p>
                    <p className="font-semibold text-green-600">Active</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={handleShopifySync}
                  disabled={shopifySyncing}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-card font-semibold hover:from-primary-700 hover:to-accent-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {shopifySyncing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Syncing Products...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Sync Products from Shopify
                    </>
                  )}
                </button>

                {shopifyProducts.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-card p-4">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Products Ready for AI Analysis
                    </h3>
                    <p className="text-sm text-blue-800 mb-4">
                      {shopifyProducts.length} products have been synced. AI audits will now test how AI systems describe your products, check pricing accuracy, and verify product availability.
                    </p>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Sample products synced:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {shopifyProducts.slice(0, 5).map((product) => (
                          <li key={product.id}>{product.title}</li>
                        ))}
                        {shopifyProducts.length > 5 && (
                          <li className="text-blue-600">+ {shopifyProducts.length - 5} more products</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-card p-4 text-sm text-gray-700">
                  <p className="font-medium mb-2">What happens when you sync:</p>
                  <ul className="space-y-1">
                    <li>✓ All products, variants, and pricing are imported</li>
                    <li>✓ Product descriptions are stored for accuracy checking</li>
                    <li>✓ Auto-generates audit prompts based on your products</li>
                    <li>✓ Updates Source of Truth with latest product data</li>
                    <li>✓ Enables AI to verify product information accuracy</li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-card p-6 text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h3 className="font-semibold text-red-900 mb-2">Unable to Connect to Shopify</h3>
              <p className="text-sm text-red-800 mb-4">
                Check that your Shopify credentials are correctly configured in your .env file.
              </p>
              <button
                onClick={testShopifyConnection}
                className="px-4 py-2 bg-red-600 text-white rounded-card hover:bg-red-700 transition-colors"
              >
                Retry Connection
              </button>
            </div>
          )}
        </div>
      )}

      {/* SFTP Sync Panel */}
      {connectionMethod === 'sftp' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">SFTP File Integration</h2>
              <p className="text-gray-600">
                Connect via SFTP to sync product data files (JSON, CSV, XML, YAML)
              </p>
            </div>
            {sftpConnected && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                Configured
              </div>
            )}
          </div>

          {sftpConnected ? (
            <>
              <div className="bg-gray-50 rounded-card p-6 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Available Files</p>
                    <p className="font-semibold text-gray-900">{sftpFiles.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Supported Formats</p>
                    <p className="font-semibold text-gray-900">JSON, CSV, XML, YAML</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Status</p>
                    <p className="font-semibold text-green-600">Active</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={testSftpConnection}
                  disabled={sftpTesting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-card font-semibold hover:bg-gray-50 transition-all disabled:opacity-50"
                >
                  {sftpTesting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Testing Connection...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Test Connection & Refresh Files
                    </>
                  )}
                </button>

                {sftpFiles.length > 0 && (
                  <div className="bg-white border-2 border-gray-200 rounded-card p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select File to Sync
                    </label>
                    <select
                      value={selectedFile}
                      onChange={(e) => setSelectedFile(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none mb-4"
                    >
                      <option value="">-- Select a file --</option>
                      {sftpFiles.map((file) => (
                        <option key={file.name} value={file.name}>
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={handleSftpSync}
                      disabled={!selectedFile || sftpSyncing}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-card font-semibold hover:from-primary-700 hover:to-accent-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sftpSyncing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Syncing File...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          Sync Selected File to Firestore
                        </>
                      )}
                    </button>
                  </div>
                )}

                <div className="bg-gray-50 rounded-card p-4 text-sm text-gray-700">
                  <p className="font-medium mb-2">What happens when you sync:</p>
                  <ul className="space-y-1">
                    <li>✓ File is downloaded from your SFTP server</li>
                    <li>✓ Data is parsed based on file format (JSON/CSV/XML/YAML)</li>
                    <li>✓ Records are stored in Firestore for analysis</li>
                    <li>✓ Data becomes available for AI accuracy audits</li>
                    <li>✓ Can be used to populate Source of Truth fields</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-card p-4">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Configuration
                  </h3>
                  <p className="text-sm text-blue-800">
                    SFTP credentials are configured in{' '}
                    <a href="/settings" className="underline font-medium">Settings</a>.
                    Update host, port, username, password, and remote path as needed.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-card p-6 text-center">
              <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
              <h3 className="font-semibold text-yellow-900 mb-2">SFTP Not Configured</h3>
              <p className="text-sm text-yellow-800 mb-4">
                Configure your SFTP credentials in Settings to enable file sync.
              </p>
              <a
                href="/settings"
                className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-card hover:bg-yellow-700 transition-colors"
              >
                Go to Settings
              </a>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Form */}
      {connectionMethod === 'manual' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Name *
              </label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="Acme Corp"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="We provide enterprise project management software for agile teams..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This is used to verify AI's understanding of what your brand does
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Founding Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Founding Date
                </label>
                <input
                  type="text"
                  value={foundingDate}
                  onChange={(e) => setFoundingDate(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="2015"
                />
              </div>

              {/* Headquarters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Headquarters
                </label>
                <input
                  type="text"
                  value={headquarters}
                  onChange={(e) => setHeadquarters(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="San Francisco, CA"
                />
              </div>

              {/* Employees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Employees
                </label>
                <input
                  type="text"
                  value={employees}
                  onChange={(e) => setEmployees(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="50-100"
                />
              </div>
            </div>

            {/* Products */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Products</label>
              {products.map((product, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={product}
                    onChange={(e) => {
                      const updated = [...products];
                      updated[index] = e.target.value;
                      setProducts(updated);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Product name"
                  />
                  {products.length > 1 && (
                    <button
                      onClick={() => setProducts(products.filter((_, i) => i !== index))}
                      className="p-3 text-red-600 hover:bg-red-50 rounded-card"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setProducts([...products, ''])}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </button>
            </div>

            {/* Pricing */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pricing</label>
              {pricing.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item.product}
                    onChange={(e) => {
                      const updated = [...pricing];
                      updated[index].product = e.target.value;
                      setPricing(updated);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Product/Plan name"
                  />
                  <input
                    type="text"
                    value={item.price}
                    onChange={(e) => {
                      const updated = [...pricing];
                      updated[index].price = e.target.value;
                      setPricing(updated);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="£499/month"
                  />
                  {pricing.length > 1 && (
                    <button
                      onClick={() => setPricing(pricing.filter((_, i) => i !== index))}
                      className="p-3 text-red-600 hover:bg-red-50 rounded-card"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setPricing([...pricing, { product: '', price: '' }])}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Pricing
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Used to detect pricing inaccuracies in AI responses
              </p>
            </div>

            {/* Key Differentiators */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Key Differentiators
              </label>
              {keyDifferentiators.map((diff, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={diff}
                    onChange={(e) => {
                      const updated = [...keyDifferentiators];
                      updated[index] = e.target.value;
                      setKeyDifferentiators(updated);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Real-time collaboration features"
                  />
                  {keyDifferentiators.length > 1 && (
                    <button
                      onClick={() =>
                        setKeyDifferentiators(keyDifferentiators.filter((_, i) => i !== index))
                      }
                      className="p-3 text-red-600 hover:bg-red-50 rounded-card"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setKeyDifferentiators([...keyDifferentiators, ''])}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Differentiator
              </button>
            </div>

            {/* Certifications */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certifications & Compliance
              </label>
              {certifications.map((cert, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={cert}
                    onChange={(e) => {
                      const updated = [...certifications];
                      updated[index] = e.target.value;
                      setCertifications(updated);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-card focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="SOC 2 Type II, GDPR Compliant"
                  />
                  {certifications.length > 1 && (
                    <button
                      onClick={() => setCertifications(certifications.filter((_, i) => i !== index))}
                      className="p-3 text-red-600 hover:bg-red-50 rounded-card"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setCertifications([...certifications, ''])}
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Certification
              </button>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving || !brandName}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-card font-semibold hover:from-primary-700 hover:to-accent-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Save Source of Truth
                </>
              )}
            </button>
            {sourceData && (
              <p className="text-center text-sm text-gray-500 mt-3">
                Last updated: {sourceData.updatedAt.toDate().toLocaleString()}
              </p>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
